import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { cta } from '../../content';
import { Arrow, Logo, SmartLink, cx } from '../../ui';
import { vbCss } from './styles';

const LINKS = [
  { label: 'Platform', href: '/platform' },
  { label: 'Industries', href: '/industries' },
  { label: 'Customers', href: '/customers' },
  { label: 'Trust', href: '/why-trust-us' },
  { label: 'Pricing', href: '/pricing' },
];

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on();
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    // The menu is hidden by CSS from 1024px; close it so the body scroll lock is released too.
    const wide = () => window.innerWidth >= 1024 && setOpen(false);
    window.addEventListener('keydown', esc);
    window.addEventListener('resize', wide);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', esc);
      window.removeEventListener('resize', wide);
    };
  }, [open]);

  return (
    <header className="vb sticky top-0 z-50">
      <style>{vbCss}</style>
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-[60] focus:rounded-full focus:bg-white focus:px-4 focus:py-2">
        Skip to content
      </a>
      <div
        className="transition-[background-color,box-shadow] duration-200"
        style={{
          background: scrolled || open ? 'rgba(255,255,255,.92)' : '#fff',
          backdropFilter: 'saturate(1.4) blur(12px)',
          WebkitBackdropFilter: 'saturate(1.4) blur(12px)',
          boxShadow: scrolled ? '0 1px 0 #E4E3F0, 0 8px 24px -18px rgba(24,19,67,.25)' : '0 1px 0 transparent',
        }}
      >
        <div className="wrap flex h-[68px] items-center gap-8">
          <SmartLink href="/" aria-label="BrandedMark home" className="shrink-0">
            <Logo size={32} />
          </SmartLink>
          <nav aria-label="Main" className="vb-navlinks items-center gap-1">
            {LINKS.map((l) => {
              const active = pathname === l.href || pathname.startsWith(l.href + '/');
              return (
                <SmartLink
                  key={l.href}
                  href={l.href}
                  className={cx('rounded-full px-3.5 py-2 text-[15px] font-medium transition-colors', active ? 'bg-bm-alt text-bm-indigo' : 'text-bm-ink/80 hover:bg-bm-alt hover:text-bm-indigo')}
                >
                  {l.label}
                </SmartLink>
              );
            })}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <SmartLink href={cta.primaryHref + '?src=nav-b'} className="btn btn-go btn-sm !px-4">
              {cta.primary}
            </SmartLink>
            <button
              type="button"
              className="vb-burger -mr-2 grid h-11 w-11 place-items-center rounded-full text-bm-indigo hover:bg-bm-alt"
              aria-expanded={open}
              aria-controls="vb-mobile-menu"
              aria-label={open ? 'Close menu' : 'Open menu'}
              onClick={() => setOpen((o) => !o)}
            >
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                {open ? (
                  <path d="M5 5l12 12M17 5 5 17" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                ) : (
                  <path d="M3.5 7h15M3.5 15h15" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div id="vb-mobile-menu" className="fixed inset-x-0 bottom-0 top-[68px] z-40 overflow-y-auto bg-white vb-mmenu">
          <nav aria-label="Mobile" className="wrap pb-10 pt-4">
            <ul>
              {LINKS.map((l) => (
                <li key={l.href} className="border-b border-bm-line">
                  <SmartLink href={l.href} className="flex items-center justify-between py-4 text-[22px] font-bold tracking-[-0.02em] text-bm-indigo">
                    {l.label}
                    <Arrow />
                  </SmartLink>
                </li>
              ))}
              <li className="border-b border-bm-line">
                <SmartLink href="/blog/" className="flex items-center justify-between py-4 text-[22px] font-bold tracking-[-0.02em] text-bm-indigo">
                  Blog
                  <Arrow />
                </SmartLink>
              </li>
            </ul>
            <div className="mt-8">
              <SmartLink href={cta.primaryHref + '?src=nav-b-mobile'} className="btn btn-go w-full">
                {cta.primary}
              </SmartLink>
            </div>
            <p className="mt-6 text-[14px] text-bm-muted">30 minutes. Bring one product. Not a sales script.</p>
          </nav>
        </div>
      )}
    </header>
  );
}
